'use client';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Select, { components } from 'react-select'; // Make sure to import components
import { Plus, Save, X, Clock, DollarSign, FlaskConical } from 'lucide-react';
import { Container, Row, Card, Input, Label } from "reactstrap";
import { Col } from "react-bootstrap";
import { Breadcrumbs } from '../../AbstractElements';
import { Link, useNavigate } from 'react-router-dom';
import { useLoader } from '../../_helper/LoaderContext';
import { addTest, globalTest, masterDepartment, masterParameter, masterSample, masterTest } from './service/test-service';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';

// Custom Checkbox Option Component
const Option = (props) => {
    return (
        <components.Option {...props}>
            <div className="d-flex align-items-center">
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    className="me-2"
                    style={{ pointerEvents: 'none' }}
                />
                <label className="m-0 cursor-pointer w-100">{props.label}</label>
            </div>
        </components.Option>
    );
};

const customStyles = {
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected || state.isFocused ? '' : '',
        color: '#333',
        ':active': {
            backgroundColor: '',
        },
    }),
};

export default function AddTest() {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        trigger,
        watch
    } = useForm({
        mode: 'onTouched',
    });

    const { setLoading } = useLoader();
    const navigate = useNavigate();

    const [departmentOption, setDepartment] = useState([]);
    const [sample, setSample] = useState([]);
    const [testMethods, setTestMethods] = useState([]);
    const [parameterOptions, setParameterOptions] = useState([]);
    const [test, setTest] = useState([]);
    const [selectedTest, setSelectedTest] = useState(null);
    const [imported, setImported] = useState("add")
    const [parameterGroups, setParameterGroups] = useState([
        { id: Date.now(), groupName: '', parameters: [], errors: { groupName: '', parameters: '' } }
    ]);

    const [userData, setUserData] = useState({})

    useEffect(() => {
        getDepartmentMaster();
        getSampleMaster();
        getTestMaster();
        getParameterMaster();
        const data = JSON.parse(localStorage.getItem("currentLab"))
        setUserData(data)
        getGlobalTest()

        // Register custom fields safely without overriding Select's onChange
        register('department', { required: 'Please select any department' });
        register('sampleType', { required: 'Please select any sample type' });
        register('testMethod', { required: 'Please select any test method' });
    }, [register]);

    const getGlobalTest = async () => {
        setLoading(true);
        try {
            const res = await globalTest()
            // console.log(res)
            if (res.data.success == true) {
                const formattedOptions = res.data.data.map(item => ({
                    value: item._id || item.id,  // Use consistent ID
                    label: item.name,
                    data: item
                }));
                setTest(formattedOptions);
            }
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const getDepartmentMaster = async () => {
        setLoading(true);
        try {
            const res = await masterDepartment();
            if (res.data.success) setDepartment(res.data.data.data);
        } catch (error) { console.log(error); }
        finally { setLoading(false); }
    };

    const getSampleMaster = async () => {
        setLoading(true);
        try {
            const res = await masterSample();
            if (res.data.success) setSample(res.data.data);
        } catch (error) { console.log(error); }
        finally { setLoading(false); }
    };

    const getTestMaster = async () => {
        setLoading(true);
        try {
            const res = await masterTest();
            if (res.data.success) setTestMethods(res.data.data);
        } catch (error) { console.log(error); }
        finally { setLoading(false); }
    };

    const getParameterMaster = async () => {
        setLoading(true);
        try {
            const res = await masterParameter();
            if (res.data.success) {
                const formatted = res.data.data.map(item => ({
                    value: item._id,
                    label: item.name,
                    fullData: item
                }));
                setParameterOptions(formatted);
            }
        } catch (error) { console.log(error); }
        finally { setLoading(false); }
    };

    const addParameterGroup = () => {
        setParameterGroups([
            ...parameterGroups,
            { id: Date.now(), groupName: '', parameters: [], errors: { groupName: '', parameters: '' } }
        ]);
    };

    const removeParameterGroup = (id) => {
        if (parameterGroups.length === 1) {
            toast.warn("At least one parameter group is required");
            return;
        }
        setParameterGroups(parameterGroups.filter(g => g.id !== id));
    };

    const updateGroupName = (id, value) => {
        setParameterGroups(prev =>
            prev.map(g =>
                g.id === id
                    ? { ...g, groupName: value, errors: { ...g.errors, groupName: '' } }
                    : g
            )
        );
    };

    const updateGroupParameters = (id, selected) => {
        setParameterGroups(prev => prev.map(g => {
            if (g.id === id) {
                const error = (selected && selected.length > 0) ? '' : 'Select at least one parameter';
                return { ...g, parameters: selected || [], errors: { ...g.errors, parameters: error } };
            }
            return g;
        }));
    };

    const validateAllGroups = () => {
        let valid = true;

        setParameterGroups(prev =>
            prev.map(g => {
                const paramError = g.parameters.length > 0 ? '' : 'Select at least one parameter';
                if (paramError) valid = false;

                return {
                    ...g,
                    errors: {
                        ...g.errors,
                        parameters: paramError,
                        groupName: ''
                    }
                };
            })
        );

        return valid;
    };

    const onSubmit = async (data) => {
        if (!validateAllGroups()) {
            toast.warning("Please fix parameter group errors");
            return;
        }
        if (parameterGroups.some(g => g.parameters.length === 0)) {
            toast.warning("Each group must have at least one parameter");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                name: data.testName,
                code: data.testCode,
                departmentId: data.department,
                departmentName: departmentOption.find(d => d._id === data.department)?.name || "",
                sampleTypeId: data.sampleType,
                sampleTypeName: sample.find(s => s._id === data.sampleType)?.name || "",
                methodId: data.testMethod,
                methodName: testMethods.find(t => t._id === data.testMethod)?.name || "",
                labId: userData.labId,
                cost: Number(data.price),
                turnaroundTime: {
                    value: Number(data.tatValue),
                    unit: data.tatUnit || "hours"
                },
                interpretation: data.interpretation || "",
                parameterGroups: parameterGroups.map(group => ({
                    groupName: group.groupName.trim(),
                    parameters: group.parameters.map(p => ({
                        parameterId: p.value,
                        name: p.label,
                        code: p.fullData.code || "",
                        unitValue: p.fullData.unitValue || "",
                        type: p.fullData.type || "quantitative",
                        referenceRanges: p.fullData.referenceRanges && p.fullData.referenceRanges.length > 0
                            ? p.fullData.referenceRanges
                            : [
                                {
                                    gender: "all",
                                    ageCategory: "adult",
                                    minValue: 0,
                                    maxValue: 0
                                }
                            ]
                    }))
                }))
            };

            const res = await addTest(payload);
            if (res.data.success) {
                toast.success("Test added successfully!");
                reset();
                navigate(`${process.env.PUBLIC_URL}/list-test`);
            }
        } catch (error) {
            console.log(error.response?.data?.message);
            // toast.warning(error.response?.data?.message || "Failed to save test");
        } finally {
            setLoading(false);
        }
    };

    const handleRadio = (e) => {
        const value = e.target.value;
        setImported(value);

        if (value === "add") {
            setSelectedTest(null);
            reset();
            setParameterGroups([
                { id: Date.now(), groupName: '', parameters: [], errors: { groupName: '', parameters: '' } }
            ]);
        }
    };

    const findLabIdByMasterId = (masterId, masterField = 'masterDepartmentId') => {
        if (!masterId) return '';
        const found = departmentOption.find(item => item[masterField] === masterId);
        return found?._id || '';
    };

    const findSampleLabId = (masterId) => {
        if (!masterId) return '';
        const found = sample.find(item => item.masterSampleTypeId === masterId);
        return found?._id || '';
    };

    const findMethodLabId = (masterId) => {
        if (!masterId) return '';
        const found = testMethods.find(item => item.masterMethodologyId === masterId);
        return found?._id || '';
    };

    const handleTestChange = async (selectedOption) => {
        setSelectedTest(selectedOption);

        if (!selectedOption) {
            reset(); // Clear form
            setParameterGroups([{ id: Date.now(), groupName: '', parameters: [], errors: { groupName: '', parameters: '' } }]);
            return;
        }

        const testData = selectedOption.data;
        const masterDeptId = testData.departmentId;
        const masterSampleId = testData.sampleTypeId;
        const masterMethodId = testData.methodId;
        const labDeptId = findLabIdByMasterId(masterDeptId, 'masterDepartmentId');
        const labSampleId = findSampleLabId(masterSampleId);
        const labMethodId = findMethodLabId(masterMethodId);

        // Step 1: Set all values with correct options
        setValue('testName', testData.name || '', { shouldValidate: true, shouldDirty: true });
        setValue('testCode', testData.code || '', { shouldValidate: true, shouldDirty: true });
        setValue('price', testData.cost || '', { shouldValidate: true });
        setValue('tatValue', testData.turnaroundTime?.value || '', { shouldValidate: true });
        setValue('tatUnit', testData.turnaroundTime?.unit || 'hours', { shouldValidate: true });
        setValue('interpretation', testData.interpretation || '');

        setValue('department', labDeptId, { shouldValidate: true, shouldDirty: true });
        setValue('sampleType', labSampleId, { shouldValidate: true, shouldDirty: true });
        setValue('testMethod', labMethodId, { shouldValidate: true, shouldDirty: true });

        await trigger();

        // Step 4: Import parameter groups
        if (testData.parameterGroups && testData.parameterGroups.length > 0) {
            const importedGroups = testData.parameterGroups.map((group, index) => ({
                id: Date.now() + index,
                groupName: group.groupName || '',
                parameters: group.parameters.map(param => {
                    const fullParam = parameterOptions.find(p =>
                        p.value === (param.parameterId?._id || param.parameterId || param._id)
                    );
                    return fullParam || {
                        value: param.parameterId || param._id,
                        label: param.name,
                        fullData: param
                    };
                }),
                errors: { groupName: '', parameters: '' }
            }));
            setParameterGroups(importedGroups);
        }
    };

    const departmentSelectOptions = departmentOption.map(d => ({
        value: d._id,
        label: d.name
    }));

    const sampleSelectOptions = sample.map(s => ({
        value: s._id,
        label: s.name
    }));

    const methodSelectOptions = testMethods.map(t => ({
        value: t._id,
        label: t.name
    }));

    const getAvailableParametersForGroup = (currentGroupId) => {
        // Get all selected parameter values from OTHER groups
        const selectedInOtherGroups = parameterGroups
            .filter(g => g.id !== currentGroupId)
            .flatMap(g => g.parameters.map(p => p.value));

        // Filter out those from the master list
        return parameterOptions.filter(option =>
            !selectedInOtherGroups.includes(option.value)
        );
    };

    return (
        <>
            <Breadcrumbs mainTitle="Add Test" parent="Home" subParent="Test List" title="Add Test" />
            <Container fluid={true}>
                <Row>
                    <Col>
                        <Card>
                            <div className="card-body p-4">
                                {/* <div className="d-flex tw-space-x-4 mb-3">
                                    <div className="radio radio-primary">
                                        <Input
                                            id="customer-radio-add"
                                            type="radio"
                                            name="customerType"
                                            value="add"
                                            checked={imported === "add"}
                                            onChange={handleRadio}
                                        />
                                        <Label for="customer-radio-add" className="mb-0 pe-2">
                                            Manual
                                        </Label>
                                    </div>
                                    <div className="radio radio-primary">
                                        <Input
                                            id="customer-radio-gloabal"
                                            type="radio"
                                            name="customerType"
                                            value="gloabal"
                                            checked={imported === "gloabal"}
                                            onChange={handleRadio}
                                        />
                                        <Label for="customer-radio-gloabal" className="mb-0 pe-2">
                                            Import From Database
                                        </Label>
                                    </div>
                                    {imported === "gloabal" && (
                                        <div className="flex-grow-1" style={{ maxWidth: '500px' }}>
                                            <Select
                                                options={test}
                                                value={selectedTest}
                                                onChange={handleTestChange}
                                                placeholder="Search and select a global test template..."
                                                isClearable
                                                isSearchable
                                            />
                                        </div>
                                    )}
                                </div> */}
                                {imported === "add" ? (
                                    <form onSubmit={handleSubmit(onSubmit)} noValidate>

                                        {/* Row 1 */}
                                        <div className="row g-4 mb-4">
                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold">Test Name <span className="text-danger">*</span></label>
                                                <input
                                                    {...register('testName', {
                                                        required: 'Test name is required',
                                                        minLength: { value: 3, message: 'Minimum 3 characters' }
                                                    })}
                                                    type="text"
                                                    className={`form-control form-control-lg rounded-3 ${errors.testName ? 'is-invalid' : ''}`}
                                                    placeholder="e.g. Comprehensive Metabolic Panel"
                                                />
                                                {errors.testName && <div className="invalid-feedback">{errors.testName.message}</div>}
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold">Test Code <span className="text-danger">*</span></label>
                                                <input
                                                    {...register('testCode', {
                                                        required: 'Test code is required',
                                                    })}
                                                    type="text"
                                                    className={`form-control form-control-lg rounded-3 ${errors.testCode ? 'is-invalid' : ''}`}
                                                    placeholder="e.g. CMP001"
                                                />
                                                {errors.testCode && <div className="invalid-feedback">{errors.testCode.message}</div>}
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold">Department <span className="text-danger">*</span>
                                                    <i className="bi bi-info-circle ms-2 tw-cursor-pointer" data-tooltip-id="name-tooltip"></i>
                                                    <Tooltip id="name-tooltip" place="top" content='Clinical department responsible for this test.' />
                                                </label>

                                                <Select
                                                    options={departmentSelectOptions}
                                                    value={departmentSelectOptions.find(opt => opt.value === watch('department')) || null}
                                                    onChange={(option) => setValue('department', option ? option.value : '', { shouldValidate: true })}
                                                    placeholder="Search department..."
                                                    isSearchable
                                                    isClearable
                                                    filterOption={(option, input) =>
                                                        option.label.toLowerCase().includes(input.toLowerCase())
                                                    }
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                />
                                                {errors.department && <div className="text-danger tw-text-[0.875em]">{errors.department.message}</div>}
                                            </div>
                                        </div>

                                        {/* Row 2 */}
                                        <div className="row g-4 mb-4">
                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold">Sample Type <span className="text-danger">*</span>
                                                    <i className="bi bi-info-circle ms-2 tw-cursor-pointer" data-tooltip-id="sample-tooltip"></i>
                                                    <Tooltip id="sample-tooltip" place="top" content='Type of biological sample required for testing.' />
                                                </label>

                                                <Select
                                                    options={sampleSelectOptions}
                                                    value={sampleSelectOptions.find(opt => opt.value === watch('sampleType')) || null}
                                                    onChange={(option) => setValue('sampleType', option ? option.value : '', { shouldValidate: true })}
                                                    placeholder="Search sample type..."
                                                    isSearchable
                                                    isClearable
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                />
                                                {errors.sampleType && <div className="text-danger tw-text-[0.875em]">{errors.sampleType.message}</div>}
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold">Test Method <span className="text-danger">*</span>
                                                    <i className="bi bi-info-circle ms-2 tw-cursor-pointer" data-tooltip-id="test-tooltip"></i>
                                                    <Tooltip id="test-tooltip" place="top" content='Laboratory technique used to perform this test.' />
                                                </label>

                                                <Select
                                                    options={methodSelectOptions}
                                                    value={methodSelectOptions.find(opt => opt.value === watch('testMethod')) || null}
                                                    onChange={(option) => setValue('testMethod', option ? option.value : '', { shouldValidate: true })}
                                                    placeholder="Search method..."
                                                    isSearchable
                                                    isClearable
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                />
                                                {errors.testMethod && <div className="text-danger tw-text-[0.875em]">{errors.testMethod.message}</div>}
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold">Price <span className="text-danger">*</span></label>
                                                <input
                                                    {...register('price', {
                                                        required: 'Price is required',
                                                        pattern: { value: /^\d+(\.\d{1,2})?$/, message: 'Valid amount e.g. 1800 or 1800.50' }
                                                    })}
                                                    type="text"
                                                    className={`form-control form-control-lg rounded-3 ${errors.price ? 'is-invalid' : ''}`}
                                                    placeholder="e.g. 1800.00"
                                                />
                                                {errors.price && <div className="invalid-feedback">{errors.price.message}</div>}
                                            </div>
                                        </div>

                                        {/* TAT */}
                                        <div className="row g-4 mb-4">
                                            <div className="col-md-4">
                                                <label className="form-label fw-semibold d-flex align-items-center gap-2">
                                                    <Clock size={20} className="text-warning" /> Turnaround Time (TAT) <span className="text-danger">*</span>
                                                </label>
                                                <div className="d-flex">
                                                    <input
                                                        {...register('tatValue', {
                                                            required: 'Enter value',
                                                            pattern: { value: /^\d+$/, message: 'Only numbers' }
                                                        })}
                                                        type="text"
                                                        className={`form-control rounded-start ${errors.tatValue ? 'is-invalid' : ''}`}
                                                        placeholder="6"
                                                        style={{ borderTopRightRadius: '0px', borderBottomRightRadius: '0px' }}
                                                    />
                                                    <select
                                                        {...register('tatUnit', { required: 'Select unit' })}
                                                        className={`form-select tw-bg-gray-300 rounded-end ${errors.tatUnit ? 'is-invalid' : ''}`}
                                                        style={{ width: '140px', borderTopLeftRadius: '0px', borderBottomLeftRadius: '0px' }}
                                                    >
                                                        <option value="">Unit</option>
                                                        <option value="minutes">Minutes</option>
                                                        <option value="hours">Hours</option>
                                                        <option value="days">Days</option>
                                                    </select>
                                                </div>
                                                {errors.tatValue && <div className="invalid-feedback d-block">{errors.tatValue.message}</div>}
                                                {errors.tatUnit && <div className="invalid-feedback d-block">{errors.tatUnit.message}</div>}
                                            </div>

                                            <div className="col-md-8">
                                                <label className="form-label fw-semibold">Interpretation (Optional)</label>
                                                <textarea
                                                    {...register('interpretation')}
                                                    className="form-control"
                                                    rows="1"
                                                    placeholder="Add clinical interpretation..."
                                                />
                                            </div>
                                        </div>

                                        {/* Dynamic Parameter Groups */}
                                        <div className="mb-5">
                                            <label className="form-label fw-semibold d-flex align-items-center gap-2 mb-3">
                                                <FlaskConical size={20} className="text-primary" />
                                                Parameter Groups
                                            </label>

                                            {parameterGroups.map((group) => (
                                                <div key={group.id} className="rounded-4 p-4 mb-4 position-relative" style={{ border: "2px solid #1b255b" }}>
                                                    {parameterGroups.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeParameterGroup(group.id)}
                                                            className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 mt-2 me-2"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}

                                                    <div className="row g-3">
                                                        <div className="col-md-4">
                                                            <label className="form-label fw-semibold">Group Name </label>
                                                            <input
                                                                type="text"
                                                                value={group.groupName}
                                                                onChange={(e) => updateGroupName(group.id, e.target.value)}
                                                                className={`form-control ${group.errors.groupName ? 'is-invalid' : ''}`}
                                                                placeholder="e.g. LIVER FUNCTION"
                                                            />
                                                            {group.errors.groupName && <div className="invalid-feedback">{group.errors.groupName}</div>}
                                                        </div>

                                                        <div className="col-md-8">
                                                            <label className="form-label fw-semibold">Parameters <span className="text-danger">*</span></label>
                                                            <Select
                                                                isMulti
                                                                isSearchable
                                                                closeMenuOnSelect={false}
                                                                hideSelectedOptions={false}
                                                                options={getAvailableParametersForGroup(group.id)}  // ← Now filtered!
                                                                value={group.parameters}
                                                                onChange={(selected) => updateGroupParameters(group.id, selected)}
                                                                placeholder="Search and select parameters..."
                                                                classNamePrefix="react-select"
                                                                components={{ Option }}
                                                                styles={customStyles}
                                                            />
                                                            {/* <Select
                                                                isMulti
                                                                isSearchable
                                                                closeMenuOnSelect={false}
                                                                hideSelectedOptions={false}
                                                                options={parameterOptions}
                                                                value={group.parameters}
                                                                onChange={(selected) => updateGroupParameters(group.id, selected)}
                                                                placeholder="Search and select parameters..."
                                                                classNamePrefix="react-select"
                                                                components={{ Option }} // This enables checkboxes
                                                                styles={customStyles}
                                                            /> */}
                                                            {group.errors.parameters && (
                                                                <small className="text-danger d-block mt-1">{group.errors.parameters}</small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="col-md-12 tw-text-end">
                                                <button type="button" onClick={addParameterGroup} className="btn btn-outline-primary">
                                                    Add Another Group
                                                </button>
                                            </div>
                                        </div>

                                        {/* Submit Buttons */}
                                        <div className="d-flex justify-content-end gap-3">
                                            <button type="submit" className="btn btn-primary">
                                                Save Test
                                            </button>
                                            <Link to={`${process.env.PUBLIC_URL}/list-test`}>
                                                <button type="button" className="btn btn-secondary">Cancel</button>
                                            </Link>
                                        </div>
                                    </form>
                                ) : (
                                    <>
                                        {selectedTest !== null && (
                                            <form onSubmit={handleSubmit(onSubmit)} noValidate>

                                                {/* Row 1 */}
                                                <div className="row g-4 mb-4">
                                                    <div className="col-md-4">
                                                        <label className="form-label fw-semibold">Test Name <span className="text-danger">*</span></label>
                                                        <input
                                                            {...register('testName', {
                                                                required: 'Test name is required',
                                                                minLength: { value: 3, message: 'Minimum 3 characters' }
                                                            })}
                                                            type="text"
                                                            className={`form-control form-control-lg rounded-3 ${errors.testName ? 'is-invalid' : ''}`}
                                                            placeholder="e.g. Comprehensive Metabolic Panel"
                                                        />
                                                        {errors.testName && <div className="invalid-feedback">{errors.testName.message}</div>}
                                                    </div>

                                                    <div className="col-md-4">
                                                        <label className="form-label fw-semibold">Test Code <span className="text-danger">*</span></label>
                                                        <input
                                                            {...register('testCode', {
                                                                required: 'Test code is required',
                                                            })}
                                                            type="text"
                                                            className={`form-control form-control-lg rounded-3 ${errors.testCode ? 'is-invalid' : ''}`}
                                                            placeholder="e.g. CMP001"
                                                        />
                                                        {errors.testCode && <div className="invalid-feedback">{errors.testCode.message}</div>}
                                                    </div>

                                                    <div className="col-md-4">
                                                        <label className="form-label fw-semibold">Department sdfsd<span className="text-danger">*</span></label>
                                                        {/* <select
                                                            {...register('department', { required: 'Please select department' })}
                                                            className={`form-select form-select-lg rounded-3 ${errors.department ? 'is-invalid' : ''}`}
                                                        >
                                                            <option value="">Choose Department</option>
                                                            {departmentOption.map(d => (
                                                                <option key={d._id} value={d._id}>{d.name}</option>
                                                            ))}
                                                        </select> */}
                                                        <Select
                                                            options={departmentSelectOptions}
                                                            value={departmentSelectOptions.find(opt => opt.value === watch('department')) || null}
                                                            onChange={(option) => setValue('department', option ? option.value : '', { shouldValidate: true })}
                                                            placeholder="Search department..."
                                                            isSearchable
                                                            isClearable
                                                            filterOption={(option, input) =>
                                                                option.label.toLowerCase().includes(input.toLowerCase())
                                                            }
                                                            className="react-select-container"
                                                            classNamePrefix="react-select"
                                                        />
                                                        {errors.department && <div className="invalid-feedback">{errors.department.message}</div>}
                                                    </div>
                                                </div>

                                                {/* Row 2 */}
                                                <div className="row g-4 mb-4">
                                                    <div className="col-md-4">
                                                        <label className="form-label fw-semibold">Sample Type <span className="text-danger">*</span></label>
                                                        <Select
                                                            options={sampleSelectOptions}
                                                            value={sampleSelectOptions.find(opt => opt.value === watch('sampleType')) || null}
                                                            onChange={(option) => setValue('sampleType', option ? option.value : '', { shouldValidate: true })}
                                                            placeholder="Search sample type..."
                                                            isSearchable
                                                            isClearable
                                                            className="react-select-container"
                                                            classNamePrefix="react-select"
                                                        />
                                                        {/* <select
                                                            {...register('sampleType', { required: 'Please select sample type' })}
                                                            className={`form-select form-select-lg rounded-3 ${errors.sampleType ? 'is-invalid' : ''}`}
                                                        >
                                                            <option value="">Choose Sample</option>
                                                            {sample.map(s => (
                                                                <option key={s._id} value={s._id}>{s.name}</option>
                                                            ))}
                                                        </select> */}
                                                        {errors.sampleType && <div className="invalid-feedback">{errors.sampleType.message}</div>}
                                                    </div>

                                                    <div className="col-md-4">
                                                        <label className="form-label fw-semibold">Test Method <span className="text-danger">*</span></label>
                                                        {/* <select
                                                            {...register('testMethod', { required: 'Please select test method' })}
                                                            className={`form-select form-select-lg rounded-3 ${errors.testMethod ? 'is-invalid' : ''}`}
                                                        >
                                                            <option value="">Choose Method</option>
                                                            {testMethods.map(t => (
                                                                <option key={t._id} value={t._id}>{t.name}</option>
                                                            ))}
                                                        </select> */}
                                                        <Select
                                                            options={methodSelectOptions}
                                                            value={methodSelectOptions.find(opt => opt.value === watch('testMethod')) || null}
                                                            onChange={(option) => setValue('testMethod', option ? option.value : '', { shouldValidate: true })}
                                                            placeholder="Search method..."
                                                            isSearchable
                                                            isClearable
                                                            className="react-select-container"
                                                            classNamePrefix="react-select"
                                                        />
                                                        {errors.testMethod && <div className="invalid-feedback">{errors.testMethod.message}</div>}
                                                    </div>

                                                    <div className="col-md-4">
                                                        <label className="form-label fw-semibold">Price <span className="text-danger">*</span></label>
                                                        <input
                                                            {...register('price', {
                                                                required: 'Price is required',
                                                                pattern: { value: /^\d+(\.\d{1,2})?$/, message: 'Valid amount e.g. 1800 or 1800.50' }
                                                            })}
                                                            type="text"
                                                            className={`form-control form-control-lg rounded-3 ${errors.price ? 'is-invalid' : ''}`}
                                                            placeholder="e.g. 1800.00"
                                                        />
                                                        {errors.price && <div className="invalid-feedback">{errors.price.message}</div>}
                                                    </div>
                                                </div>

                                                {/* TAT */}
                                                <div className="row g-4 mb-4">
                                                    <div className="col-md-4">
                                                        <label className="form-label fw-semibold d-flex align-items-center gap-2">
                                                            <Clock size={20} className="text-warning" /> Turnaround Time (TAT) <span className="text-danger">*</span>
                                                        </label>
                                                        <div className="d-flex">
                                                            <input
                                                                {...register('tatValue', {
                                                                    required: 'Enter value',
                                                                    pattern: { value: /^\d+$/, message: 'Only numbers' }
                                                                })}
                                                                type="text"
                                                                className={`form-control rounded-start ${errors.tatValue ? 'is-invalid' : ''}`}
                                                                placeholder="6"
                                                                style={{ borderTopRightRadius: '0px', borderBottomRightRadius: '0px' }}
                                                            />
                                                            <select
                                                                {...register('tatUnit', { required: 'Select unit' })}
                                                                className={`form-select tw-bg-gray-300 rounded-end ${errors.tatUnit ? 'is-invalid' : ''}`}
                                                                style={{ width: '140px', borderTopLeftRadius: '0px', borderBottomLeftRadius: '0px' }}
                                                            >
                                                                <option value="">Unit</option>
                                                                <option value="minutes">Minutes</option>
                                                                <option value="hours">Hours</option>
                                                                <option value="days">Days</option>
                                                            </select>
                                                        </div>
                                                        {errors.tatValue && <div className="invalid-feedback d-block">{errors.tatValue.message}</div>}
                                                        {errors.tatUnit && <div className="invalid-feedback d-block">{errors.tatUnit.message}</div>}
                                                    </div>

                                                    <div className="col-md-8">
                                                        <label className="form-label fw-semibold">Interpretation (Optional)</label>
                                                        <textarea
                                                            {...register('interpretation')}
                                                            className="form-control"
                                                            rows="1"
                                                            placeholder="Add clinical interpretation..."
                                                        />
                                                    </div>
                                                </div>

                                                {/* Dynamic Parameter Groups */}
                                                <div className="mb-5">
                                                    <label className="form-label fw-semibold d-flex align-items-center gap-2 mb-3">
                                                        <FlaskConical size={20} className="text-primary" />
                                                        Parameter Groups
                                                    </label>

                                                    {parameterGroups.map((group) => (
                                                        <div key={group.id} className="rounded-4 p-4 mb-4 position-relative" style={{ backgroundColor: '#fff', border: "2px solid #1b255b" }}>
                                                            {parameterGroups.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeParameterGroup(group.id)}
                                                                    className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 mt-2 me-2"
                                                                >
                                                                    <X size={16} />
                                                                </button>
                                                            )}

                                                            <div className="row g-3">
                                                                <div className="col-md-4">
                                                                    <label className="form-label fw-semibold">Group Name </label>
                                                                    <input
                                                                        type="text"
                                                                        value={group.groupName}
                                                                        onChange={(e) => updateGroupName(group.id, e.target.value)}
                                                                        className={`form-control ${group.errors.groupName ? 'is-invalid' : ''}`}
                                                                        placeholder="e.g. LIVER FUNCTION"
                                                                    />
                                                                    {group.errors.groupName && <div className="invalid-feedback">{group.errors.groupName}</div>}
                                                                </div>

                                                                <div className="col-md-8">
                                                                    <label className="form-label fw-semibold">Parameters <span className="text-danger">*</span></label>
                                                                    <Select
                                                                        isMulti
                                                                        isSearchable
                                                                        closeMenuOnSelect={false}
                                                                        hideSelectedOptions={false}
                                                                        options={parameterOptions}
                                                                        value={group.parameters}
                                                                        onChange={(selected) => updateGroupParameters(group.id, selected)}
                                                                        placeholder="Search and select parameters..."
                                                                        classNamePrefix="react-select"
                                                                        components={{ Option }} // This enables checkboxes
                                                                        styles={customStyles}
                                                                    />
                                                                    {group.errors.parameters && (
                                                                        <small className="text-danger d-block mt-1">{group.errors.parameters}</small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}

                                                    <button type="button" onClick={addParameterGroup} className="btn btn-outline-primary">
                                                        <Plus size={20} className="me-2" /> Add Another Group
                                                    </button>
                                                </div>

                                                {/* Submit Buttons */}
                                                <div className="d-flex justify-content-end gap-3">
                                                    <button type="submit" className="btn btn-primary">
                                                        Save Test
                                                    </button>
                                                    <Link to={`${process.env.PUBLIC_URL}/list-test`}>
                                                        <button type="button" className="btn btn-secondary">Cancel</button>
                                                    </Link>
                                                </div>
                                            </form>
                                        )}
                                    </>
                                )}
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}